import { datadogRum } from "@datadog/browser-rum";
import { useSetAtom } from "jotai";
import { LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";

import { ldIdentifiedContextKeyAtom } from "~/shared/state/feature-flags-state.ts";

// Single source of truth: runtime environment config injected via /env/config.ts
function getLaunchDarklyClientId(): string | undefined {
	const anyGlobal = globalThis as unknown as {
		_envconfig?: Record<string, unknown>;
	};
	const value = anyGlobal._envconfig?.["launchDarklyClientId"];
	return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

export type FeatureFlagsProviderProps = {
	children: ReactNode;
	/** Optional override for LD user key; defaults to anonymous */
	userKey?: string;
	/** Optional additional user attributes (e.g., email, custom) */
	userAttributes?: Record<string, unknown>;
	/** Optional override for context kind; defaults to user */
	contextKind?: string;
};

// Inner component that updates context without remounting
function ContextUpdater({
	children,
	userKey = "anonymous",
	userAttributes = {},
	contextKind = "user",
}: Readonly<FeatureFlagsProviderProps>) {
	const ldClient = useLDClient();
	const setIdentifiedContextKey = useSetAtom(ldIdentifiedContextKeyAtom);

	const contextKey = userKey || "anonymous";
	// Serialize the attributes so a fresh object with identical values (callers
	// commonly rebuild this every render) does not churn identify(). Rebuilding
	// the context from the signature keeps the memo dependent only on stable,
	// value-based inputs.
	const attributesSignature = JSON.stringify(userAttributes);
	const context = useMemo(
		() => ({
			kind: contextKind,
			key: contextKey,
			anonymous: userKey === "anonymous",
			...(JSON.parse(attributesSignature) as Record<string, unknown>),
		}),
		[contextKind, contextKey, userKey, attributesSignature],
	);

	useEffect(() => {
		if (!ldClient) return;
		let cancelled = false;
		// A new context is in flight; consumers must wait for THIS identify to
		// resolve before trusting flag values for the new context.
		setIdentifiedContextKey(null);
		void ldClient
			.identify(context)
			.then(() => {
				if (!cancelled) {
					setIdentifiedContextKey(context.key);
				}
			})
			.catch((error: unknown) => {
				// Fail closed: leave the readiness signal unset so flag consumers do
				// not act on stale/unverified values, and report the drop so it is
				// observable rather than a silent undercount.
				if (!cancelled) {
					datadogRum.addError(
						error instanceof Error ? error : new Error(String(error)),
						{ feature: "feature-flags", contextKey: context.key },
					);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [ldClient, context, setIdentifiedContextKey]);

	return children;
}

export function FeatureFlagsProvider({
	children,
	userKey = "anonymous",
	userAttributes = {},
	contextKind = "user",
}: Readonly<FeatureFlagsProviderProps>) {
	const clientSideId = getLaunchDarklyClientId();

	// Capture initial context once using ref
	const initialContextRef = useRef({
		kind: contextKind,
		key: userKey || "anonymous",
		anonymous: userKey === "anonymous",
		...userAttributes,
	});

	// If no client ID configured, render children without LaunchDarkly
	if (!clientSideId) return children;

	return (
		<LDProvider clientSideID={clientSideId} context={initialContextRef.current}>
			<ContextUpdater
				contextKind={contextKind}
				userAttributes={userAttributes}
				userKey={userKey}
			>
				{children}
			</ContextUpdater>
		</LDProvider>
	);
}
